import { createRouter, createWebHistory } from "vue-router";

import { useAuthStore } from "../stores/auth";
import { useDateStore } from "../stores/dates";
import { importChunk } from "../utils/chunk";
import AgentMemoriesView from "../views/Agent/AgentMemories.vue";
import AgentTracesView from "../views/Agent/AgentTraces.vue";
import FleetView from "../views/FleetView.vue";

const router = createRouter({
	history: createWebHistory(),
	routes: [
		{
			component: FleetView,
			name: "home",
			path: "/",
		},
		{
			component: () => importChunk(() => import("../views/LoginView.vue")),
			name: "login",
			path: "/login",
		},
		{
			component: () => importChunk(() => import("../views/Dashboard/DashboardView.vue")),
			name: "dashboard",
			path: "/dashboard",
		},
		{
			component: () => importChunk(() => import("../views/MyStuffView.vue")),
			name: "my-stuff",
			path: "/my-stuff",
		},
		{
			component: () => importChunk(() => import("../views/SubmissionsView.vue")),
			name: "submissions",
			path: "/submissions",
		},
		{
			component: () => importChunk(() => import("../views/RenewalsView.vue")),
			name: "renewals",
			path: "/renewals",
		},
		{
			component: () => importChunk(() => import("../views/Agent/AgentView.vue")),
			name: "agent",
			path: "/agent",
			redirect: "/agent/traces",
			children: [
				{
					component: AgentTracesView,
					name: "agent-traces",
					path: "/agent/traces",
				},
				{
					component: AgentMemoriesView,
					name: "agent-memories",
					path: "/agent/memories",
				},
			],
		},
		{
			component: () => importChunk(() => import("../views/Submissions/SubmissionDetailView.vue")),
			name: "submission-detail",
			path: "/submissions/:id",
		},
		{
			component: () => importChunk(() => import("../views/Aircraft/AircraftDetailView.vue")),
			name: "aircraft",
			path: "/aircraft/:tailNumber",
		},
		{
			component: () => importChunk(() => import("../views/DemoView.vue")),
			name: "demo",
			path: "/demo",
		},
		{
			component: () => importChunk(() => import("../views/Flights/FlightDetailView.vue")),
			name: "flight-detail",
			path: "/flights/:flightId",
		},
		{
			component: () => importChunk(() => import("../views/Flights/AirsyncView.vue")),
			name: "airsync-flight-detail",
			path: "/flights/:flightId/airsync",
		},
		{
			component: () => importChunk(() => import("../views/Quotes/QuoteDetailView.vue")),
			name: "quote-template",
			path: "/quote-template",
			meta: {
				requiresAuth: true,
			},
		},
		{
			component: () => importChunk(() => import("../views/Quotes/QuoteDetailView.vue")),
			name: "quote-detail",
			path: "/quotes/:quoteId",
			meta: {
				requiresAuth: true,
			},
		},
	
		{
			component: () => importChunk(() => import("../views/Quotes/Bind/BindView.vue")),
			name: "quote-bind",
			path: "/quotes/:quoteId/bind",
			meta: {
				requiresAuth: false,
			},
		},
		{
			component: () => importChunk(() => import("../views/Airport/AirportDetailView.vue")),
			name: "airport",
			path: "/airports/:code",
		},
		{
			component: () => importChunk(() => import("../views/Brokerages/BrokerageDetailView.vue")),
			name: "brokerage",
			path: "/brokerages/:id",
		},
		{
			component: () => importChunk(() => import("../views/Accounts/AccountDetailView.vue")),
			name: "account",
			path: "/accounts/:id",
		},
		{
			component: () => importChunk(() => import("../views/Policies/PolicyListView.vue")),
			name: "policies",
			path: "/policies",
		},
		{
			component: () => importChunk(() => import("../views/Policies/PolicyDetailView.vue")),
			name: "policy",
			path: "/policies/:id",
		},
		{
			component: () => importChunk(() => import("../views/Leads/LeadListView.vue")),
			name: "leads",
			path: "/leads",
		},
		{
			component: () => importChunk(() => import("../views/Leads/LeadListView.vue")),
			name: "lead",
			path: "/leads/:id",
		},
		{
			component: () => importChunk(() => import("../views/Incidents/IncidentsListView.vue")),
			name: "incidents",
			path: "/incidents",
		},
		{
			component: () => importChunk(() => import("../views/Incidents/IncidentDetailView.vue")),
			name: "incident",
			path: "/incidents/:id",
		},
		{
			component: () => importChunk(() => import("../views/RouteView.vue")),
			name: "routes",
			path: "/routes",
		},
		{
			component: () => importChunk(() => import("../views/FleetBoard/FleetBoardView.vue")),
			name: "fleet-board",
			path: "/fleet-board",
			meta: {
				hideNavbar: true,
				hideToasts: true,
			},
		},
		{
			component: () => importChunk(() => import("../views/UwBoard/UwBoardView.vue")),
			name: "uw-board",
			path: "/uw-board",
			meta: {
				hideNavbar: true,
				hideToasts: true,
			},
		},
		{
			component: () => importChunk(() => import("../views/RotorWingDemoView.vue")),
			name: "rotor-wing-demo",
			path: "/rotor-wing-demo",
			meta: {
				requiresAuth: false,
				hideNavbar: true,
			},
		},
		{
			component: () => importChunk(() => import("../views/QuickbooksAuthView.vue")),
			name: "quickbooks-auth",
			path: "/quickbooks-auth",
			meta: { requiresAuth: true },
		},
		{
			component: () => importChunk(() => import("../views/QuickbooksAuthCallbackView.vue")),
			name: "quickbooks-auth-callback",
			path: "/quickbooks-auth/callback",
			meta: { requiresAuth: true },
		},
		{
			component: () => importChunk(() => import("../views/ResetPasswordView.vue")),
			name: "reset-password",
			path: "/reset-password",
			meta: {
				requiresAuth: false,
				hideNavbar: true,
			},
		},
		{
			component: () => importChunk(() => import("../views/AccountSetupView.vue")),
			name: "account-setup",
			path: "/setup-account",
			meta: {
				requiresAuth: false,
				hideNavbar: true,
			},
		},
		{
			component: () => importChunk(() => import("../views/Forms/PilotWarranty.vue")),
			name: "pilot-warranty-formgen",
			path: "/formgen/pilot-warranty",
			meta: {
				requiresAuth: false,
				hideNavbar: true,
			},
		},
		{
			component: () => importChunk(() => import("../views/Forms/AircraftAdditionQuote.vue")),
			name: "aircraft-addition-quote-formgen",
			path: "/formgen/aircraft-addition-quote",
			meta: {
				requiresAuth: false,
				hideNavbar: true,
			},
		},
		{
			component: () => importChunk(() => import("../views/Forms/QuoteDocument.vue")),
			name: "quote-document-formgen",
			path: "/formgen/quote-document/:documentId",
			meta: {
				requiresAuth: false,
				hideNavbar: true,
			},
		},
	],
});

router.beforeEach((to) => {
	if (useAuthStore().fleetBoardOnly && to.path !== "/fleet-board" && to.name !== "uw-board") {
		return "/fleet-board";
	}
});

router.afterEach((to, from, failure) => {
	if (!failure && to.path !== from.path) {
		useDateStore().reset();
	}
});

export default router;
